import { CapabilityCard } from "modules/capabilities/components/List/CapabilityCard"
import { useCampaignCapabilitiesQuery } from "modules/capabilities/hooks/useCampaignCapabilitiesQuery"
import { useUpdateCapabilityActivationMutation } from "modules/capabilities/hooks/useUpdateCapabilityActivationMutation"
import { useMemo } from "react"
import { CapabilitiesResponseDataSchema } from "schema"
import z from "zod"

type CapabilityRowProps = {
  capability: z.infer<typeof CapabilitiesResponseDataSchema>[number]
  campaignId: string
}

function CapabilityRow({ capability, campaignId }: CapabilityRowProps) {
  const { mutateAsync: updateActivation, isPending: isActivationPending } =
    useUpdateCapabilityActivationMutation()

  const isActiveForCampaign = useMemo(
    () =>
      capability.campaignAssignation === "AllCampaigns" ||
      capability.campaignAssignation === "CampaignFlow" ||
      capability.campaignIds.includes(campaignId),
    [campaignId, capability],
  )

  const hasOverride = capability.campaignConfigOverride !== null

  const handleToggle = async (enabled: boolean) => {
    if (capability.campaignAssignation !== "HandPicked") return

    const newCampaignIds = enabled
      ? [...capability.campaignIds, campaignId]
      : capability.campaignIds.filter((id) => id !== campaignId)

    await updateActivation({
      id: capability.id,
      campaignAssignation: "HandPicked",
      campaignIds: newCampaignIds,
    })
  }

  return (
    <CapabilityCard
      capability={capability}
      campaignContext={{
        campaignId,
        isActiveForCampaign,
        hasOverride,
        isTogglePending: isActivationPending,
        onToggle: handleToggle,
      }}
    />
  )
}

type Props = {
  id: string
}

export function Capabilities({ id }: Props) {
  const { data: capabilities } = useCampaignCapabilitiesQuery({ campaignId: id })

  if (!capabilities?.length) return null

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      {capabilities.map((capability) => (
        <CapabilityRow key={capability.id} capability={capability} campaignId={id} />
      ))}
    </div>
  )
}
